import type { Incident, Resource, EventStatus } from '../types';

export type CommandActionType = 'LA' | 'AL' | 'ZTR' | 'OI' | 'MSG' | 'STATUS';

export interface ParsedCommandAction {
  type: CommandActionType;
  resource?: string;
  incidentRef?: string;
  messageText?: string;
  status?: EventStatus;
  statusReason?: string;
}

const COMMAND_PREFIX = /^(LA|AL|ZTR|OI|MSG|STATUS)\//i;

const EVENT_STATUS_VALUES: EventStatus[] = ['open', 'supervision', 'closed'];

function normaliseEventStatus(value: string): EventStatus | null {
  const normalised = value.trim().toLowerCase().replace(/\s+/g, '_');
  return EVENT_STATUS_VALUES.includes(normalised as EventStatus)
    ? (normalised as EventStatus)
    : null;
}

export function looksLikeCommandAction(input: string): boolean {
  return COMMAND_PREFIX.test(input.trim());
}

/** Parse action bar short codes. Returns null if not a recognised command. */
export function parseCommandAction(input: string): ParsedCommandAction | null {
  const trimmed = input.trim();
  if (!trimmed || !looksLikeCommandAction(trimmed)) return null;

  const upper = trimmed.toUpperCase();
  if (upper.startsWith('MSG/')) {
    const rest = trimmed.slice(4);
    const slashIdx = rest.indexOf('/');
    if (slashIdx <= 0) return null;
    const resource = rest.slice(0, slashIdx).trim();
    const messageText = rest.slice(slashIdx + 1).trim();
    if (!resource || !messageText) return null;
    return { type: 'MSG', resource, messageText };
  }

  if (upper.startsWith('STATUS/')) {
    const rest = trimmed.slice(7);
    const parts = rest.split('/').map((part) => part.trim()).filter(Boolean);
    if (parts.length < 2) return null;

    const status = normaliseEventStatus(parts[0]);
    if (!status) return null;

    return {
      type: 'STATUS',
      status,
      statusReason: parts.slice(1).join('/').trim(),
    };
  }

  const slash = trimmed.indexOf('/');
  if (slash <= 0) return null;
  const cmd = trimmed.slice(0, slash).trim().toUpperCase();
  const arg = trimmed.slice(slash + 1).trim();
  if (!arg) return null;

  switch (cmd) {
    case 'LA':
      return { type: 'LA', resource: arg };
    case 'AL':
      return { type: 'AL', resource: arg };
    case 'ZTR':
      return { type: 'ZTR', resource: arg };
    case 'OI':
      return { type: 'OI', incidentRef: arg };
    default:
      return null;
  }
}

export function findResourceByQuery(resources: Resource[], query: string): Resource | undefined {
  const q = query.trim().toLowerCase();
  if (!q) return undefined;
  return (
    resources.find((r) => r.id.toLowerCase() === q) ||
    resources.find((r) => r.name.toLowerCase() === q) ||
    resources.find((r) => r.id.toLowerCase().includes(q)) ||
    resources.find((r) => r.name.toLowerCase().includes(q))
  );
}

/** Match incident by full id, suffix, or numeric fragment (e.g. 23, 545). */
export function findIncidentByQuery(incidents: Incident[], query: string): Incident | undefined {
  const q = query.trim().toLowerCase();
  if (!q) return undefined;

  const exact = incidents.find((i) => i.id.toLowerCase() === q);
  if (exact) return exact;

  const contains = incidents.filter((i) => i.id.toLowerCase().includes(q));
  if (contains.length === 1) return contains[0];

  const digits = q.replace(/\D/g, '');
  if (digits) {
    const digitMatches = incidents.filter((i) => i.id.replace(/\D/g, '').includes(digits));
    if (digitMatches.length === 1) return digitMatches[0];
    if (digitMatches.length > 1) {
      const suffix = digitMatches.find((i) => i.id.toLowerCase().endsWith(q));
      if (suffix) return suffix;
    }
  }

  return contains[0];
}

export interface ResourceHistoryIncident {
  id: string;
  title: string;
  status: string;
  severity: string;
  reportedAt: string;
  isCurrent?: boolean;
  location?: string;
}

export interface ActionBarShortCode {
  code: string;
  description: string;
}

export const ACTION_BAR_SHORT_CODES: ActionBarShortCode[] = [
  { code: 'LA/[resource]', description: 'Open last assigned incident for that resource' },
  { code: 'AL/[resource]', description: 'Popup list of every incident the resource has attended' },
  { code: 'ZTR/[resource]', description: 'Zoom map to resource tracking (warns if offline)' },
  { code: 'OI/[incident]', description: 'Open incident by full or partial number (e.g. 23, 545)' },
  { code: 'MSG/[resource]/[text]', description: 'Send a private message without opening chat' },
  {
    code: 'STATUS/[status]/[reason]',
    description: 'Change the active event status (open, supervision, closed) with a reason',
  },
];
